import React, {
  ReactElement, useCallback, useEffect, useRef, useState
} from 'react'
import ErrorBoundaryWrapper from './ErrorBoundaryWrapper'
import BasketEmpty from './BasketEmpty'
import SingleWidgetManager from '@hubbox/single-widget-manager'
import BasketIntro from './BasketIntro'
import BasketBuyItem from './BasketBuyItem'
import BasketHireItem from './BasketHireItem'
import BasketFlash from './BasketFlash'
import BasketSectionHeader from './BasketSectionHeader'
import BasketSummary from './BasketSummary'
import DeliveryOptions from './DeliveryOptions'
import DeliverySurchargeWarning from './DeliverySurchargeWarning'
import debounce, { debouncing } from '../../common/debounce'
import { DeliveryOption, DeliveryOptions as EDeliveryOptions } from './types/DeliveryOptions'
import type {
  BasketItemModel, BasketModel, ShippingClassModel, DiscountLineModel,
  MismatchTubeResult, CourierService, HubboxCollectPoint
} from './types/models'
import type { BasketProps, UpdateBasketResponseData } from './types/BasketProps'
import type {
  KioskCollectTodayPayload, KioskCollectTodayState
} from './types/KioskCollectToday'
import type { Nullable } from '../../types/nullable'
import DiscountLine from './DiscountLine'
import ChooseDeliveryDate from './ChooseDeliveryDate'
import DateHelpers from './helpers/DateHelpers'
import CheckoutOptions from './CheckoutOptions'
import updateHeaderBasketSummary from '../../common/updateHeaderBasketSummary'
import type { HTTPMethod } from './types/HTTPMethods'
import CrossSell from './CrossSell'
import type { CrossSellResult } from './CrossSell'
import DialogModal from './DialogModal'
import CrossSellModal from './CrossSellModal'
import MismatchModal from './MismatchModal'
import { generateAndSubmitCuttingToolDataFrom } from '../../metal_store/submitCuttingToolBar'
import ChooseDeliverySlot from './ChooseDeliverySlot'
import DiscountInput from './DiscountInput'
import { trackKlaviyoStartedCheckout } from '../../common/tracking/klaviyo/klaviyo_started_checkout'

const Basket = ({
  railsEnv, amazonPayCheckout, amazonPayload, basket, collectionAvailable, crossSellProviders, crossSellRules,
  deliveryOption, deliveryPostcodeLabel, deliveryPostcode, sessionDiscountCode,
  discountLines, emptyBasket, includeVat, intro, orderTotal, paypalExpressCheckout,
  shippingAmount, shippingAmountIncVat, shippingClass, shippingQuoteNeeded,
  subtotal, subtotalIncVat, vatTotal, deliveryDates, deliveryDate, hasHireItems, profit,
  courierServices, courierService, hubboxCollectPoint, kioskCollectToday
}: BasketProps): ReactElement => {
  const [basketState, setBasketState] = useState<Nullable<BasketModel>>(null)
  const [
    deliveryOptionState, setDeliveryOptionState
  ] = useState<DeliveryOption>(EDeliveryOptions.Blank)
  const [deliveryPostcodeState, setDeliveryPostcodeState] = useState<string>('')
  const [hubboxCollectPointState, setHubboxCollectPointState] = useState<HubboxCollectPoint | null>(null)
  const [
    discountLinesState, setDiscountLinesState
  ] = useState<Nullable<DiscountLineModel[]>>(null)
  const [requestsInFlight, setRequestsInFlight] = useState<number>(0)
  const [orderTotalState, setOrderTotalState] = useState<string>('0')
  const [shippingAmountState, setShippingAmountState] = useState<string>('0')
  const [shippingAmountIncVatState, setShippingAmountIncVatState] = useState<string>('0')
  const [shippingClassState, setShippingClassState] = useState<Nullable<ShippingClassModel>>(null)
  const [hasHireItemsState, setHasHireItemsState] = useState<boolean>(false)
  const [shippingQuoteNeededState, setShippingQuoteNeededState] = useState<boolean>(false)
  const [deliveryDateState, setDeliveryDateState] = useState<Nullable<Date>>(null)
  const [deliveryDatesState, setDeliveryDatesState] = useState<Date[]>(null)
  const [discountCode, setDiscountCode] = useState('')
  const [discountCodeState, setDiscountCodeState] = useState<string>('')
  const [discountCodeValid, setDiscountCodeValid] = useState<boolean | null>(null)
  const [subtotalState, setSubtotalState] = useState<string>('0')
  const [subtotalIncVatState, setSubtotalIncVatState] = useState<string>('0')
  const [hubBox, setHubBox] = useState<SingleWidgetManager | null>(null)
  const [vatTotalState, setVatTotalState] = useState<string>('0')
  const [basketFlashMessage, setBasketFlashMessage] = useState<Nullable<string>>(null)
  const [showCrossSellModal, setShowCrossSellModal] = useState<boolean>(false)
  const [crossSellResults, setCrossSellResults] = useState<CrossSellResult[]>([])
  const [intendedCheckoutPath, setIntendedCheckoutPath] = useState<string>('')
  const [profitState, setProfitState] = useState<string>('')
  const [courierServicesState, setCourierServicesState] = useState<CourierService[]>([])
  const [showBasketModal, setShowBasketModal] = useState<boolean>(false)
  const [selectedCourierService, setSelectedCourierService] = useState<Nullable<CourierService>>(null)
  const [kioskCollectTodayState, setKioskCollectTodayState] =
    useState<KioskCollectTodayState | null>(null)

  const token: string = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.getAttribute('content')

  const transformKioskCollectToday = (
    state?: KioskCollectTodayPayload | null
  ): KioskCollectTodayState | null => {
    if (!state) {
      return null
    }

    const date = DateHelpers.transformDeliveryDate(state.date)
    return date ? { ...state, date } : null
  }

  const optionsForFetch = (body: BodyInit, method: HTTPMethod): RequestInit => ({
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': token
    },
    body,
    credentials: 'same-origin'
  })

  // Sets all our state from initial props.
  const initializeBasket = (): void => {
    setBasketState(basket)
    setDeliveryOptionState(deliveryOption)
    setDeliveryPostcodeState(deliveryPostcode)
    setHubboxCollectPointState(hubboxCollectPoint)
    setDiscountLinesState(discountLines)
    setOrderTotalState(orderTotal)
    setShippingAmountIncVatState(shippingAmountIncVat)
    setShippingAmountState(shippingAmount)
    setShippingClassState(shippingClass)
    setShippingQuoteNeededState(shippingQuoteNeeded)
    setSubtotalState(subtotal)
    setSubtotalIncVatState(subtotalIncVat)
    setVatTotalState(vatTotal)
    setDeliveryDatesState(DateHelpers.transformDeliveryDates(deliveryDates))
    setDeliveryDateState(DateHelpers.transformDeliveryDate(deliveryDate))
    setDiscountCodeValid(sessionDiscountCode === '' ? null : true)
    setDiscountCode(sessionDiscountCode)
    setHasHireItemsState(hasHireItems)
    setProfitState(profit)
    setCourierServicesState(courierServices)
    setSelectedCourierService(courierService)
    setKioskCollectTodayState(transformKioskCollectToday(kioskCollectToday))
  }

  const basketItemQuantityReducer = (acc: object, item: BasketItemModel): object => {
    acc[item.id] = item.quantity
    return acc
  }

  const basketItemHireIdReducer = (acc: object, item: BasketItemModel): object => {
    acc[item.id] = item.hire_price_id
    return acc
  }

  const cuttingInstructionsReducer = (acc: object, item: BasketItemModel): object => {
    if (item.product.cuttable) {
      acc[item.product.id] = item.cutting_instructions
    }
    return acc
  }

  const offcutsRequiredReducer = (acc: object, item: BasketItemModel): object => {
    if (item.product.cuttable) {
      const yesId = item.product.offcuts_required.choices.find((c) => c.name === 'Yes').id
      const noId = item.product.offcuts_required.choices.find((c) => c.name === 'No').id
      acc[item.product.id] = item.offcuts_required ? yesId : noId
    }
    return acc
  }

  // Updates our state with state from the backend.
  const updateStateFromBackend = (data: UpdateBasketResponseData): void => {
    if (debouncing()) {
      // Ignoring update due to a new debounce in progress.
      // This prevents the user's current edits being undone but is safe
      // because we know there will be another update soon.
      return
    }
    setBasketState(data.basket)
    setDeliveryOptionState(data.delivery_option as DeliveryOption)
    setDeliveryPostcodeState(data.delivery_postcode)
    setHubboxCollectPointState(data.hubbox_collect_point)
    setDiscountLinesState(data.discount_lines)
    setOrderTotalState(data.order_total)
    setShippingAmountState(data.shipping_amount)
    setShippingAmountIncVatState(data.shipping_amount_inc_vat)
    setShippingClassState(data.shipping_class)
    setShippingQuoteNeededState(data.shipping_quote_needed)
    setSubtotalState(data.subtotal)
    setSubtotalIncVatState(data.subtotal_inc_vat)
    setVatTotalState(data.vat_total)
    setDeliveryDateState(DateHelpers.transformDeliveryDate(data.delivery_date))
    setDeliveryDatesState(DateHelpers.transformDeliveryDates(data.delivery_dates))
    setKioskCollectTodayState(transformKioskCollectToday(data.kiosk_collect_today))
    setDiscountCode(data.discount_code)
    if (data.discount_code !== '') {
      setDiscountCodeValid(true)
    } else if (data.discount_code_valid === false) {
      setDiscountCodeValid(false)
    }

    setBasketFlashMessage(data.update_flash_message)
    setProfitState(data.profit)
    setCourierServicesState(data.courier_services)
    setSelectedCourierService(data.courier_service)
    const hasItems = data.basket.basket_items.some(
      (item: BasketItemModel) => item.hire_price_id !== null
    )
    setHasHireItemsState(hasItems)
  }

  const basketItemQuantityFilter = (item: BasketItemModel): boolean => (
    item.quantity !== null
  )
  const basketItemHireIdFilter = (item: BasketItemModel): boolean => (
    item.hire_price_id !== null
  )

  const updateSelectionWithUrl = async (url: string, bodyObject, method: HTTPMethod = 'POST'): Promise<void> => {
    const body = JSON.stringify(bodyObject)
    setRequestsInFlight((prev) => prev + 1)
    try {
      const response = await fetch(url, optionsForFetch(body, method))
      const { data } = await response.json()
      updateStateFromBackend(data)
    } catch (error) {
      // Local basket state may now be incorrect.
      // Maybe we should encourage the user to refresh the page?
      console.error(error, method, url, body)
    }
    setRequestsInFlight((prev) => prev - 1)
  }

  useEffect(() => {
    if (basketState?.suitable_for_local_collection && hubBox === null) {
      initializeHubbox()
    }
  }, [basketState])

  const initializeHubbox = () => {
    const iframeUrl = railsEnv === 'production' ? SingleWidgetManager.iframeUrls.PRODUCTION : SingleWidgetManager.iframeUrls.SANDBOX
    const hubBox = new SingleWidgetManager({
      deferRender: true, // Tells manager not to render iframe immediately
      iframeUrl,
      iframeParams: {
        showInformation: true,
        configId: 'themetalstore',
        locale: 'en-GB'
      }
    })
    setHubBox(hubBox)
  }

  const launchHubbox = () => {
    if (hubBox === null) {
      return console.error('Initialize HubBox first')
    }

    const hubboxModal: HTMLElement = document.getElementById('hubbox')
    const hubboxContainer: HTMLElement = document.getElementById('hubbox-pop-up')
    const hubboxCloseButton: HTMLElement = document.getElementById('hubbox-close-pop-up')

    if (hubboxModal) {
      hubboxModal.appendChild(hubBox.iframeElem)
    }

    if (deliveryPostcodeState !== '') {
      hubBox.events.emit(hubBox.topics.emit.MAP_SEARCH_QUERY, deliveryPostcodeState)
    }

    hubboxContainer.style.display = 'block'
    hubBox.events.subscribe(hubBox.topics.subscribe.COLLECT_POINT_CONFIRMED, onCollectPointConfirmed)

    hubboxCloseButton.addEventListener('click', closeHubbox)
  }

  const closeHubbox = () => {
    const hubboxContainer: HTMLElement = document.getElementById('hubbox-pop-up')
    if (hubboxContainer) {
      hubboxContainer.style.display = 'none'
    }
  }

  const hasOpenDay = (openingHours) => {
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    return days.some(day => openingHours[day]?.open !== 'Closed')
  }

  const onCollectPointConfirmed = (event) => {
    const { message: { hubboxCollectPointId, openingHours } } = event
    closeHubbox()
    // Ensure that chosen collect point has at least one open day
    if (hasOpenDay(openingHours)) {
      const bodyObject = { hubbox_collect_point_id: hubboxCollectPointId }
      updateSelectionWithUrl('/basket/store_hubbox_checkout_details', bodyObject)
    } else {
      setHubboxCollectPointState(null)
      return window.alert('The local pickup point you selected does not have any open days, please select another option.')
    }
  }

  const updateBasket = async (newBasketState: BasketModel): Promise<void> => {
    const basketItemQuantities = newBasketState
      .basket_items
      .filter(basketItemQuantityFilter)
      .reduce(basketItemQuantityReducer, {})

    const basketItemHirePriceIds = newBasketState
      .basket_items
      .filter(basketItemHireIdFilter)
      .reduce(basketItemHireIdReducer, {})

    const cuttingInstructions = newBasketState.basket_items.reduce(cuttingInstructionsReducer, {})
    const offcutsRequired = newBasketState.basket_items.reduce(offcutsRequiredReducer, {})

    const url = '/metal_store/basket/update'
    const bodyObject = {
      // Instead of reduce, can we filter them so that we aren't sending invalid orderlines
      cutting_instructions: cuttingInstructions,
      offcuts_required: offcutsRequired,
      qty: basketItemQuantities,
      hire_price_ids: basketItemHirePriceIds,
      update_quantities: 1,
      update_hire_price_ids: 1
    }
    updateSelectionWithUrl(url, bodyObject)
  }

  const removeItem = async (id: number): Promise<void> => {
    const url = `/basket_items/${id}`
    const bodyObject = { _method: 'delete' }
    updateSelectionWithUrl(url, bodyObject, 'DELETE')
  }

  const updateBasketDebounced = useCallback(debounce((b: BasketModel) => updateBasket(b), 500), [])

  useEffect(() => { initializeBasket() }, [])
  useEffect(() => {
    if (basketState) updateHeaderBasketSummary(basketState)
  }, [basketState, orderTotalState])

  const onHireWeeksChanged = (id: number, hirePriceId: string): void => {
    const numberHirePriceID = Number(hirePriceId)
    const basketItem = basketState.basket_items.find((item: BasketItemModel) => item.id === id)
    basketItem.hire_price_id = numberHirePriceID
    setBasketState((prevState: BasketModel): BasketModel => {
      const newState = { ...prevState, basket_items: basketState.basket_items.slice() }
      updateBasketDebounced(newState)
      return newState
    })
  }

  // Each item's quantity from before its quantity box was cleared, so that the
  // hole quantity is adjusted from the right starting point when a new
  // quantity is typed in.
  const quantitiesBeforeClearing = useRef<Record<number, number>>({})

  // If quantity of product with holes changes change the quantity of holes also
  const handleHoleQuantity = (basketItem: BasketItemModel, numberQty: number): void => {
    const holeProductSku = 'FIX.HOLE.1'
    const holes = basketItem.number_of_holes
    const holeBasketItem = basketState.basket_items.find((item: BasketItemModel) => item.product.sku === holeProductSku)
    if (holes > 0 && holeBasketItem) {
      const previousQuantity = basketItem.quantity ?? quantitiesBeforeClearing.current[basketItem.id] ?? 0
      holeBasketItem.quantity += holes * (numberQty - previousQuantity)
    }
  }

  const onQuantityChanged = (id: number, quantity: string): void => {
    const numberQty = Number(quantity)
    if (numberQty < 0) {
      return
    }

    const basketItem = basketState.basket_items.find((item: BasketItemModel) => item.id === id)
    if (quantity === '') {
      if (basketItem.quantity !== null) quantitiesBeforeClearing.current[id] = basketItem.quantity
      basketItem.quantity = null
    } else {
      handleHoleQuantity(basketItem, numberQty)
      basketItem.quantity = numberQty
      delete quantitiesBeforeClearing.current[id]
    }

    setBasketState((prevState: BasketModel): BasketModel => {
      const newState = { ...prevState, basket_items: basketState.basket_items.slice() }

      if (basketItem.quantity !== null) {
        updateBasketDebounced(newState)
      }
      return newState
    })
  }

  const onCuttingInstructionsChanged = (
    item: BasketItemModel, instructions: string, offcutsRequired: boolean
  ): void => {
    const basketItem = basketState.basket_items.find((i: BasketItemModel) => i.id === item.id)
    basketItem.cutting_instructions = instructions
    basketItem.offcuts_required = offcutsRequired

    setBasketState((prevState: BasketModel) => {
      const newState = { ...prevState, basket_items: basketState.basket_items.slice() }

      updateBasketDebounced(newState)

      return newState
    })
  }

  const onDiscountCodeSubmitted = () => {
    const bodyObject = { code: discountCodeState }
    updateSelectionWithUrl('/basket/redeem_discount', bodyObject)
  }

  const updateShippingSelectionDebounced = useCallback(
    debounce((option: string, pc: string) => {
      const bodyObject = { delivery_option: option, delivery_postcode: pc }
      updateSelectionWithUrl('/basket/update_shipping_selection', bodyObject)
    }, 500), []
  )

  const onDeliveryPostcodeChanged = (newDeliveryPostcode: string): void => {
    const pc = newDeliveryPostcode.toLocaleUpperCase()
    setDeliveryPostcodeState(pc)
    updateShippingSelectionDebounced(deliveryOptionState, pc)
  }

  const onDeliveryOptionChanged = (newDeliveryOption: DeliveryOption): void => {
    setDeliveryOptionState(newDeliveryOption)
    updateShippingSelectionDebounced(newDeliveryOption, deliveryPostcodeState)
  }

  useEffect(() => {
    if (
      deliveryDateState &&
      !kioskCollectTodayState?.selected &&
      !deliveryDatesState?.map((d) => String(d)).includes(String(deliveryDateState))
    ) {
      onDeliveryDateChanged(null)
    }
  }, [deliveryDatesState, kioskCollectTodayState])

  const onCourierServiceChanged = (newCourierService: CourierService): void => {
    setSelectedCourierService(newCourierService)
    const bodyObject = { courier_service_id: newCourierService.id }
    updateSelectionWithUrl('/basket/update_courier_service', bodyObject)
  }

  const onDeliveryDateChanged = (
    newDeliveryDate: Date | null, kioskCollectTodaySelection = false
  ): void => {
    setDeliveryDateState(newDeliveryDate)
    // Send the same date-only contract the server gives us; basket dates are
    // calendar entries and /basket/amend_delivery_date accepts YYYY-MM-DD.
    const deliveryDate = DateHelpers.formatDeliveryDate(newDeliveryDate)
    const bodyObject = {
      delivery_date: deliveryDate,
      kiosk_collect_today: kioskCollectTodaySelection
    }
    updateSelectionWithUrl('/basket/amend_delivery_date', bodyObject)
  }

  const postcodeRequired = (): boolean => deliveryPostcodeState.length === 0 &&
    deliveryOptionState === EDeliveryOptions.DeliverToPostcode

  const toggleEarlyDelivery = (): void => {
    const currentEarlyDeliveryValue = basketState.early_delivery
    setBasketState((prevState: BasketModel): BasketModel => ({
      ...prevState,
      early_delivery: !prevState.early_delivery
    }))
    const bodyObject = { early_delivery: currentEarlyDeliveryValue ? '0' : '1' }
    updateSelectionWithUrl('/basket/update_early_delivery', bodyObject)
  }

  const onItemRemoved = (id: number, discountId: number): void => {
    let newBasketItems: BasketItemModel[]

    if (discountId) {
      newBasketItems = basketState.basket_items.filter(
        (item: BasketItemModel) => item.discount_id !== discountId
      )
    } else {
      newBasketItems = basketState.basket_items.filter(
        (item: BasketItemModel) => item.id !== id
      )
    }

    setBasketState((prevState: BasketModel) => ({ ...prevState, basket_items: newBasketItems }))
    removeItem(id)
  }

  const proceedToCheckout = (crossSells: CrossSellResult[], fullPath: string): void => {
    setCrossSellResults(crossSells)
    if (crossSells.length === 0) {
      if (basketState) trackKlaviyoStartedCheckout(basketState)
      window.location.href = fullPath
    } else {
      setIntendedCheckoutPath(fullPath)
      setShowCrossSellModal(true)
    }
  }

  const onCheckout = (fullPath: string): void => {
    const crossSells = CrossSell(crossSellProviders, crossSellRules, basketState.basket_items, basketState.suitable_sizes_for_tubes)
    if (crossSells.length > 0) {
      fetch('/basket/add_comment', optionsForFetch(JSON.stringify({ comment: 'Suggesting pre-checkout crossSells: ' + JSON.stringify(crossSells), emit_cross_sell_event: true }), 'POST')).then(() => {
        proceedToCheckout(crossSells, fullPath)
      })
    } else if (mismatchTubeClamps().length > 0) {
      showMismatchModal(fullPath)
    } else {
      proceedToCheckout(crossSells, fullPath)
    }
  }

  const onAddCrossSells = async (): Promise<void> => {
    const qty = {}
    await generateAndSubmitCuttingToolDataFrom('.cutToSize-innerSummary', true)
    crossSellResults.filter((crossSell) => crossSell.quantity > 0).forEach(async (crossSell) => {
      if (crossSell.product.pricing_method !== 'cut_to_size_length') {
        qty[crossSell.product.id] = crossSell.quantity
      }
    })
    const url = '/basket/add_increase_multiple'
    const bodyObject = { qty, emit_cross_sell_event: true }
    updateSelectionWithUrl(url, bodyObject)
    setShowCrossSellModal(false)
  }

  const showMismatchModal = (fullPath: string): void => {
    setIntendedCheckoutPath(fullPath)
    setShowCrossSellModal(false)
    if (mismatchTubeClamps().length > 0) {
      setShowBasketModal(true)
    } else {
      proceedToCheckout([], fullPath)
    }
  }

  type BasketItemGroup = {
    headerContent: string[];
    itemFilter: (item: BasketItemModel) => boolean;
    itemElement: (item: BasketItemModel) => ReactElement;
  }

  const buyItems: BasketItemGroup = {
    headerContent: ['Buying', 'Unit Price', 'Quantity'],
    itemFilter: (item) => !item.hire_price_id,
    itemElement: (item) => {
      return <BasketBuyItem key={item.id} item={item} onItemRemoved={onItemRemoved} onQuantityChanged={onQuantityChanged} onCuttingInstructionsChanged={onCuttingInstructionsChanged} includeVat={includeVat} />
    }
  }

  const hireItems: BasketItemGroup = {
    headerContent: ['Hiring', 'First 4 weeks hire', 'Installation'],
    itemFilter: (item) => !!item.hire_price_id,
    itemElement: (item) => {
      return <BasketHireItem key={item.id} item={item} onItemRemoved={onItemRemoved} onHireWeeksChanged={onHireWeeksChanged} includeVat={includeVat} />
    }
  }

  const groupedBasketItems = (basketItemGroup: BasketItemGroup): ReactElement[] => {
    const items = basketState.basket_items.filter(basketItemGroup.itemFilter)
    if (!items.length) { return [] }
    const itemElements = items.map((i: BasketItemModel) => basketItemGroup.itemElement(i))
    return (
      [<BasketSectionHeader key={basketItemGroup.headerContent[0]} content={basketItemGroup.headerContent} />].concat(itemElements)
    )
  }

  const basketItems = (): ReactElement[] => {
    return groupedBasketItems(hireItems).concat(groupedBasketItems(buyItems))
  }

  const renderDiscountLines = (): ReactElement[] => discountLinesState.map(
    (dl: DiscountLineModel) => !dl.is_reward && <DiscountLine key={dl.name} line={dl}/>
  )

  const canShowDeliverySlots = (): boolean => {
    if (!shippingClassState) {
      return false
    }

    if (deliveryOptionState === 'local-collection') {
      return false
    }

    // Do not show delivery slots if there is less than one option to choose from or if user has selected collection
    return !shippingClassState.is_collection && courierServicesState.length > 1
  }

  function canCheckout (): boolean {
    if (requestsInFlight > 0) {
      return false
    }

    if (!shippingClassState) {
      return false
    }

    if ((shippingClassState.choose_date && !deliveryDateState) && !hasHireItemsState) {
      return false
    }

    if (!deliveryOptionState) {
      return false
    }

    if (deliveryOptionState === EDeliveryOptions.DeliverToPostcode && !deliveryPostcodeState) {
      return false
    }

    if (deliveryOptionState === EDeliveryOptions.LocalCollection && !hubboxCollectPointState) {
      return false
    }

    const hasInvalidBasketItem = basketState.basket_items.some((basketItem) => (
      basketItem.quantity === null
    ))

    if (hasInvalidBasketItem) {
      return false
    }

    if (basketState.is_empty) {
      return false
    }

    return true
  }

  function canShowDeliveryDates (): boolean {
    if (hasHireItemsState) {
      return false
    }

    if (shippingClassState !== null && shippingClassState.choose_date) {
      return true
    }

    return false
  }

  const canExpressCheckout = (): boolean => Number(orderTotalState) < 2000 && !shippingQuoteNeededState

  if (basketState === null) {
    return null
  }

  function mismatchTubeClamps (): MismatchTubeResult[] {
    return basketState.mismatching_tube_clamps
  }

  const canToggleEarlyDelivery = () => {
    return !shippingClassState.is_collection &&
      !selectedCourierService?.am &&
      !selectedCourierService?.saturday &&
      !selectedCourierService?.sunday &&
      deliveryOptionState !== 'local-collection'
  }

  return (
    <ErrorBoundaryWrapper railsEnv={railsEnv}>
      {
        basketState.is_empty
          ? <>
            <div style={{ display: 'none' }} className={`basket-requestsInFlight-${requestsInFlight}`}></div>
            <BasketEmpty content={emptyBasket} />
            </>
          : <>
            <div style={{ display: 'none' }} className={`basket-requestsInFlight-${requestsInFlight}`}></div>
            <DialogModal
              isOpened={showCrossSellModal}
              onClose={() => setShowCrossSellModal(false)}
              >
              <CrossSellModal
                crossSellResults={crossSellResults}
                onAddCrossSells={onAddCrossSells}
                onClose={() => {
                  showMismatchModal(intendedCheckoutPath)
                }}
                onQuantityChanged={(id: number, quantity: number) => {
                  if (quantity < 0) {
                    return
                  }
                  setCrossSellResults(crossSellResults.map((crossSell) => crossSell.product.id === id ? { ...crossSell, quantity } : crossSell))
                }}
              />
            </DialogModal>
            <DialogModal
              isOpened={showBasketModal}
              onClose={() => setShowBasketModal(false)
              }
              >
              <MismatchModal
                mismatchTubeClampResults={mismatchTubeClamps()}
                fullPath={intendedCheckoutPath}
                proceedToCheckout={proceedToCheckout}
                setShowBasketModal={setShowBasketModal}
                sizesAndDiameters={basketState.sizes_and_diameters}
              />
            </DialogModal>
            <BasketIntro content={intro} />
            <section className="new-basket-table-container">
              <BasketFlash message={basketFlashMessage} />
              <div className="new-basket-table">
                { basketItems() }
                { renderDiscountLines() }
                <hr className="basketDivider" />
              </div>
              <div className="new-table-footer">
                <div className="calculate-delivery">
                  <DeliveryOptions
                    collectionAvailable={collectionAvailable}
                    deliveryOption={deliveryOptionState}
                    deliveryPostcodeLabel={deliveryPostcodeLabel}
                    deliveryPostcode={deliveryPostcodeState}
                    onDeliveryOptionChanged={onDeliveryOptionChanged}
                    onDeliveryPostcodeChanged={onDeliveryPostcodeChanged}
                    postcodeRequired={postcodeRequired}
                    launchHubbox={launchHubbox}
                    hubboxCollectPoint={hubboxCollectPointState}
                    suitableForLocalCollection={basketState.suitable_for_local_collection}
                  />
                </div>
                <BasketSummary
                  basket={basketState}
                  includeVat={includeVat}
                  orderTotal={orderTotalState}
                  shippingAmount={shippingAmountState}
                  shippingAmountIncVat={shippingAmountIncVatState}
                  shippingQuoteNeeded={shippingQuoteNeededState}
                  subtotal={subtotalState}
                  subtotalIncVat={subtotalIncVatState}
                  vatTotal={vatTotalState}
                  profit={profitState}
                />
                {canShowDeliveryDates() &&
                  <ChooseDeliveryDate
                    basket={basketState}
                    deliveryDates={deliveryDatesState}
                    deliveryDate={deliveryDateState}
                    shippingClass={shippingClassState}
                    toggleEarlyDelivery={toggleEarlyDelivery}
                    onDeliveryDateChanged={onDeliveryDateChanged}
                    canToggleEarlyDelivery={canToggleEarlyDelivery()}
                    kioskCollectToday={kioskCollectTodayState}
                  />
                }
                {canShowDeliverySlots() && canShowDeliveryDates() &&
                  <ChooseDeliverySlot
                    courierServices={courierServicesState}
                    selectedCourierService={selectedCourierService}
                    setSelectedCourierService={onCourierServiceChanged}
                  />
                }
                {!canShowDeliveryDates() &&
                  <div className="spacer"></div>
                }
                <DiscountInput
                  discountCode={discountCode}
                  discountCodeState={discountCodeState}
                  discountCodeValid={discountCodeValid}
                  onDiscountCodeSubmitted={onDiscountCodeSubmitted}
                  setDiscountCode={setDiscountCodeState}
                  setDiscountCodeValid={setDiscountCodeValid}
                />
              </div>
              <CheckoutOptions
                amazonPayload={amazonPayload}
                amazonPayCheckout={amazonPayCheckout}
                canCheckout={canCheckout}
                canExpressCheckout={canExpressCheckout}
                deliveryOptionState={deliveryOptionState}
                paypalExpressCheckout={paypalExpressCheckout}
                onCheckout={onCheckout}
                railsEnv={railsEnv}
              />
              <DeliverySurchargeWarning
                basket={basketState}
                shippingClass={shippingClassState}
                shippingQuoteNeeded={shippingQuoteNeededState}
              />
            </section>
          </>
      }
    </ErrorBoundaryWrapper>
  )
}

export default Basket
